import { useQuery, useMutation } from '@apollo/client';
import {
  NSP_CORE_INFO_LIST,
  NSP_CORE_INFO,
  NSP_CORE_INFO_BY_TYPE,
  CREATE_NSP_CORE_INFO,
  UPDATE_NSP_CORE_INFO,
  DELETE_NSP_CORE_INFO,
} from 'graphql/nspCoreInfo.gql';
import type {
  NspCoreInfo,
  CreateNspCoreInfoInput,
  UpdateNspCoreInfoInput,
} from '@custom-types/nspCoreInfo';

// Hook to get all NSP Core Info entries
export const useNspCoreInfoList = () => {
  const { data, loading, error, refetch } = useQuery<{ nspCoreInfoList: NspCoreInfo[] }>(
    NSP_CORE_INFO_LIST
  );

  return {
    nspCoreInfoList: data?.nspCoreInfoList || [],
    loading,
    error,
    refetch,
  };
};

// Hook to get a specific NSP Core Info by ID
export const useNspCoreInfo = (nspCoreInfoId: number) => {
  const { data, loading, error, refetch } = useQuery<{ nspCoreInfo: NspCoreInfo }>(NSP_CORE_INFO, {
    variables: { nspCoreInfoId },
    skip: !nspCoreInfoId,
  });

  return {
    nspCoreInfo: data?.nspCoreInfo,
    loading,
    error,
    refetch,
  };
};

// Hook to get a specific NSP Core Info by entry type
export const useNspCoreInfoByType = (entryType: string) => {
  const { data, loading, error, refetch } = useQuery<{ nspCoreInfoByType: NspCoreInfo }>(
    NSP_CORE_INFO_BY_TYPE,
    {
      variables: { entryType },
      skip: !entryType,
    }
  );

  return {
    nspCoreInfo: data?.nspCoreInfoByType,
    loading,
    error,
    refetch,
  };
};

// Hook for create mutation
export const useCreateNspCoreInfo = () => {
  const [createNspCoreInfo, { loading, error }] = useMutation<
    { createNspCoreInfo: NspCoreInfo },
    { input: CreateNspCoreInfoInput }
  >(CREATE_NSP_CORE_INFO, {
    refetchQueries: [{ query: NSP_CORE_INFO_LIST }],
  });

  return {
    createNspCoreInfo,
    loading,
    error,
  };
};

// Hook for update mutation
export const useUpdateNspCoreInfo = () => {
  const [updateNspCoreInfo, { loading, error }] = useMutation<
    { updateNspCoreInfo: NspCoreInfo },
    { nspCoreInfoId: number; input: UpdateNspCoreInfoInput }
  >(UPDATE_NSP_CORE_INFO);

  return {
    updateNspCoreInfo,
    loading,
    error,
  };
};

// Hook for delete mutation
export const useDeleteNspCoreInfo = () => {
  const [deleteNspCoreInfo, { loading, error }] = useMutation<
    { deleteNspCoreInfo: boolean },
    { nspCoreInfoId: number }
  >(DELETE_NSP_CORE_INFO, {
    refetchQueries: [{ query: NSP_CORE_INFO_LIST }],
  });

  return {
    deleteNspCoreInfo,
    loading,
    error,
  };
};
